import React, { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, ArrowRight } from "lucide-react";
import ServicePrice from "../components/ServicePrice";
import { useCatalog } from "../contexts/CatalogContext";

export default function Services() {
  const [params] = useSearchParams();
  const { categories, SEGMENTS } = useCatalog();
  const initialCat = params.get("category") || "all";
  const [activeCat, setActiveCat] = useState(initialCat);
  const [activeSeg, setActiveSeg] = useState("all");

  const orderedCategories = useMemo(() => {
    const roCategory = categories.find((c) => c.id === "ro");
    return roCategory ? [roCategory, ...categories.filter((c) => c.id !== "ro")] : categories;
  }, [categories]);

  const liveCategories = useMemo(() => orderedCategories.filter((c) => c.status !== "soon"), [orderedCategories]);
  const upcomingCategories = useMemo(() => orderedCategories.filter((c) => c.status === "soon"), [orderedCategories]);

  const visibleCategories = useMemo(() => {
    return activeCat === "all" ? liveCategories : liveCategories.filter((c) => c.id === activeCat);
  }, [activeCat, liveCategories]);

  const allBrands = Array.from(new Set(liveCategories.flatMap((c) => c.brands)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      {/* Hero */}
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Services</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 mt-3">RO Services</h1>
        <p className="mt-4 text-slate-600">Book RO installation, repair, AMC and cleaning in 60 seconds. More categories are coming soon.</p>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="category-tabs">
        {[{ id: "all", short: "All" }, ...liveCategories].map((c) => (
          <button
            key={c.id}
            data-testid={`tab-cat-${c.id}`}
            onClick={() => setActiveCat(c.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeCat === c.id
                ? "bg-[#0C4A6E] text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-[#0C4A6E]"
            }`}
          >
            {c.short}
          </button>
        ))}
      </div>

      {/* Segment filter */}
      <div className="flex flex-wrap gap-2 mb-10" data-testid="segment-tabs">
        <span className="text-xs font-semibold text-slate-500 self-center mr-1 uppercase">For:</span>
        {[{ id: "all", name: "All" }, ...SEGMENTS].map((s) => (
          <button
            key={s.id}
            data-testid={`tab-seg-${s.id}`}
            onClick={() => setActiveSeg(s.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              activeSeg === s.id
                ? "bg-[#0369A1] text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-[#0369A1]"
            }`}
          >
            {s.name}
          </button>
        ))}
      </div>

      {/* Categories */}
      <div className="space-y-16">
        {visibleCategories.map((c) => {
          const Icon = c.icon;
          return (
            <section key={c.id} data-testid={`category-section-${c.id}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: c.bg, color: c.color }}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-2xl text-slate-900">{c.name}</h2>
                  <p className="text-sm text-slate-500">{c.tagline}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {c.services.map((s) => (
                  <div key={s.id} data-testid={`service-card-${s.id}`} className="bg-white rounded-2xl p-6 border border-slate-200 card-shadow flex flex-col">
                    <div className="flex justify-between items-start gap-3">
                      <h3 className="font-display font-bold text-lg text-slate-900 leading-snug">{s.name}</h3>
                      <span className="font-display font-bold text-lg text-[#0C4A6E] whitespace-nowrap">
                        <ServicePrice service={s} />{s.id.includes("amc") && <span className="text-xs font-medium text-slate-500">/yr</span>}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">{s.desc}</p>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-3">
                      <Clock className="w-3.5 h-3.5" /> {s.time}
                    </div>
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-700 flex-1">
                      {s.includes.map((it) => (
                        <li key={it} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[#22C55E] mt-0.5 flex-shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="text-xs text-slate-500 mb-2">Brands supported</div>
                      <div className="flex flex-wrap gap-1.5">
                        {c.brands.slice(0, 4).map((b) => (
                          <span key={b} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                            {b}
                          </span>
                        ))}
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          +{c.brands.length - 4} more
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/booking?category=${c.id}&service=${s.id}${activeSeg !== "all" ? `&segment=${activeSeg}` : ""}`}
                      data-testid={`book-service-${s.id}`}
                      className="mt-5 bg-[#0369A1] hover:bg-[#075985] text-white text-center font-semibold py-2.5 rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      Book This Service <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {upcomingCategories.length > 0 && (
        <section className="mt-12 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8">
          <h2 className="font-display font-bold text-2xl text-slate-900">More categories are coming soon</h2>
          <p className="mt-2 text-sm text-slate-600">We are currently focused on RO service bookings and will expand to other home services shortly.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {upcomingCategories.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
                {c.name} • Coming soon
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Supported brands */}
      <section className="mt-20 bg-white rounded-2xl p-8 border border-slate-200" data-testid="all-brands-section">
        <h2 className="font-display font-bold text-2xl text-slate-900 text-center">All Brands We Service</h2>
        <p className="text-sm text-slate-500 text-center mt-2">Original spares • Manufacturer-trained technicians</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {allBrands.map((b) => (
            <span key={b} className="px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 font-display font-semibold text-slate-700">
              {b}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
